import type { InstrumentIdentity } from './InstrumentIdentityFields';
import type {
    InstrumentSuggestion,
    InstrumentSuggestionPayload,
} from '@/api/instrument-suggestions';

/**
 * The recut the request promises: headers and first-column labels, never a
 * cell. Mirrors the caps the api enforces again on arrival
 * (`instrument-suggestion.schema.ts`).
 */
const HEADER_LIMIT = 30;
const ROW_LABEL_LIMIT = 40;
const MAX_CHARS = 200;
const MIN_USEFUL_ROW_LABELS = 2;

const clamp = (value: string) => value.replace(/\s+/g, ' ').trim().slice(0, MAX_CHARS);

/** A first-column cell holding a score, a percentile or a rank. */
const isNumericLabel = (value: string) => /^[\d.,%\-+/\s]+$/.test(value);

const nonEmpty = (row: string[]) => row.filter((cell) => clamp(cell).length > 0);

/**
 * Splits a pasted grid into a caption, a header row and row labels.
 *
 * `grid[0]` is **not** the header — it is row zero of whatever was selected.
 * `parseClipboardGrid` does no header detection at all; "the first row becomes
 * the header" is a convention `tableBlockFromGrid` imposes afterwards. A title
 * merged across the top of the table therefore shifts everything by one, and
 * the labels the prompt depends on would come back as column headers.
 *
 * So a first row with exactly one filled cell in a wider table is read as the
 * caption it is, and the header moves down a row.
 *
 * Returns `null` when there is not enough left to ask about: with fewer than
 * two usable labels the shortlist has no evidence to be judged by, and the
 * request is not worth making.
 */
export const buildSuggestionRequest = (
    grid: string[][],
    options: { printedName?: string; patientId?: string } = {},
): InstrumentSuggestionPayload | null => {
    if (grid.length === 0) return null;

    const firstRow = grid[0] ?? [];
    const isTitleRow = firstRow.length > 1 && nonEmpty(firstRow).length === 1;

    const titleHint = isTitleRow ? clamp(nonEmpty(firstRow)[0]) : '';
    const headerIndex = isTitleRow ? 1 : 0;
    const header = (grid[headerIndex] ?? []).slice(0, HEADER_LIMIT).map(clamp).filter(Boolean);

    const rowLabels = grid
        .slice(headerIndex + 1)
        .map((row) => clamp(row[0] ?? ''))
        .filter((label) => label.length > 0 && !isNumericLabel(label))
        .slice(0, ROW_LABEL_LIMIT);

    if (rowLabels.length < MIN_USEFUL_ROW_LABELS) return null;

    const printedName = clamp(options.printedName ?? '');

    return {
        header,
        row_labels: rowLabels,
        ...(titleHint ? { title_hint: titleHint } : {}),
        ...(printedName ? { printed_name: printedName } : {}),
        ...(options.patientId ? { patient_id: options.patientId } : {}),
    };
};

/** Whether a response has anything to put on screen. */
export const hasSuggestion = (suggestion: InstrumentSuggestion): boolean =>
    suggestion.source !== 'none' && Boolean(suggestion.name);

/**
 * What `Preencher` writes. A `model` suggestion carries no citation, so it
 * leaves the reference field alone rather than clearing it — the click is
 * consent to fill in what is on screen, not to erase what is not.
 */
export const applySuggestion = (
    suggestion: InstrumentSuggestion,
    current: InstrumentIdentity,
): InstrumentIdentity => {
    if (!hasSuggestion(suggestion)) return current;

    return {
        name: suggestion.name ?? current.name,
        reference: suggestion.reference ?? current.reference,
    };
};
